import { cn } from '@/lib/cn';

export type ChipTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

const tones: Record<ChipTone, string> = {
  success: 'bg-success/10 text-success',
  warning: 'bg-warning/10 text-warning',
  danger: 'bg-danger/10 text-danger',
  info: 'bg-info/10 text-info',
  neutral: 'bg-fg-subtle/15 text-fg-muted',
};

const dotText: Record<ChipTone, string> = {
  success: 'text-success', warning: 'text-warning', danger: 'text-danger',
  info: 'text-info', neutral: 'text-fg-subtle',
};
const dotBg: Record<ChipTone, string> = {
  success: 'bg-success', warning: 'bg-warning', danger: 'bg-danger',
  info: 'bg-info', neutral: 'bg-fg-subtle',
};

/**
 * Small status indicator; color conveys tone, text always present (a11y).
 * `variant="pill"` (default) is the filled chip used across list views;
 * `variant="dot"` is the bare dot + label used by the health/API widgets
 * (absorbed from the former dashboard-only StatusPill in 4.9A).
 */
export function StatusChip({ tone, children, variant = 'pill' }: {
  tone: ChipTone;
  children: React.ReactNode;
  variant?: 'pill' | 'dot';
}) {
  if (variant === 'dot') {
    return (
      <span className={cn('inline-flex items-center gap-1.5 text-xs font-medium', dotText[tone])}>
        <span className={cn('h-2 w-2 rounded-full', dotBg[tone])} aria-hidden="true" />
        {children}
      </span>
    );
  }
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium', tones[tone])}>
      <span className={cn('h-1.5 w-1.5 rounded-full', dotBg[tone])} aria-hidden="true" />
      {children}
    </span>
  );
}
